import range from "lodash/range";

import { useRouter } from "next/router";

import { useTranslator } from "@eo-locale/react";

import dayjs from "dayjs";

import toArray from "lodash/toArray";

import { CommonDateQuestion } from "@/storybook-components";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";

import {
  removeSurveyAnswers,
  setSurveyAnswers
} from "@/redux/features/survey/slice";
import type { Question, SurveyParams } from "@/types";
import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";
import useEmbeds from "@/hooks/useEmbeds";

import googleTranslateInit from "@/helpers/google-translate-init";
import { spacing, textStyles } from "./question-styles";

interface IDateQuestion {
  question: Question;
}

const DateQuestion = ({ question }: IDateQuestion): JSX.Element => {
  const { translate, language } = useTranslator();
  const dispatch = useAppDispatch();
  const { query } = useRouter();
  const { isInsideIframe, isEmbed } = useEmbeds();
  const { region } = useAppSelector((state) => state.site.site);

  const { yearStart = 1900, yearEnd = 2051 } = query as Partial<SurveyParams>;
  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, saving, pages, questions } = survey;
  const { settings } = useAppSelector((state) => state.settings);
  const { googleTranslationEnabled } = settings;

  const error = errors[question.id] || {};
  const answer = answers[question.id]?.value ?? "";

  const options = range(Math.max(yearStart, 1900), Math.min(yearEnd, 2051));

  const handleYearChange = (e: { value: number }) => {
    if (!e) {
      // need to trigger Google Translate upon clearing the field to translate the placeholder
      googleTranslateInit(isEmbed, googleTranslationEnabled);
      dispatch(
        removeSurveyAnswers({
          questionId: question.id
        })
      );
      return;
    }

    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: { value: `${e.value}-01-01` }
      })
    );
  };

  const handleChange = (date: Date) => {
    // need to trigger Google Translate upon clearing the field to translate the placeholder
    if (date === null) googleTranslateInit(isEmbed, googleTranslationEnabled);
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: { value: date ? dayjs(date).format("YYYY-MM-DD") : "" }
      })
    );
  };

  const currentPage = pages.find((page) => page.current);
  const questionsInPage = toArray(questions).filter(
    (q) => q.pageId === currentPage.id
  );
  const numberOfQuestionsPerpage = questionsInPage.length;

  const renderYear = () => {
    const value = new Date(answer).getFullYear();
    return value ? { label: value, value: value } : null;
  };

  return (
    <CommonDateQuestion
      layoutProps={{ width: "100%" }}
      isDisabled={!!saving}
      isInvalid={!!error.code}
      isRequired={question.required}
      className={error.code ? "ehq-question-error" : null}
      spacing={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      isYearOnlyDate={question.yearOnlyDate}
      options={
        question.yearOnlyDate
          ? options.map((op) => ({ value: op, label: op }))
          : []
      }
      isClearable={true}
      isSearchable={false}
      menuPlacement="auto"
      answer={question.yearOnlyDate ? renderYear() : answer}
      contentClassName="ehq-dropdownContainer"
      locale={language === "en" ? "en-AU" : language}
      placeholder={translate("survey.selectDate")}
      dateFormat={region === "united_states" ? "MM-dd-yyyy" : "dd-MM-yyyy"}
      answerAriaLabel={translate("survey.selectDate")}
      isInsideIframe={isInsideIframe && numberOfQuestionsPerpage < 3}
      handleChange={question.yearOnlyDate ? handleYearChange : handleChange}
      size="lg"
      answerProps={{ ...textStyles?.content }}
      footerStackSpacing={spacing.footerStack}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
    />
  );
};

export default DateQuestion;
